import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import AuthService from '../services/AuthService';
import jwtDecode from 'jwt-decode';
import { User } from '../shared/interfaces/IUser';
import { IToken } from '../shared/interfaces/IToken';

interface Props {
  children?: ReactNode;
}

interface Auth {
  authenticated: boolean;
  login: (
    username: string,
    password: string,
    loading: Dispatch<SetStateAction<boolean>>
  ) => Promise<boolean>;
  logout: () => Promise<void>;
  silentLogout: () => void;
  user: User | null;
  hasRole: (realmRole: string) => boolean;
  hasResourceAccess: (type: 'realm-management' | 'account', roleName: string) => boolean;
  exceedsLimit: (limitName: string, testValue: number) => boolean;
  validUntil: () => any;
  getLimit: (limitName: string, defaultValue: number) => number;
  updateTokens: (
    access_token: string,
    refresh_token: string,
    updateUserWithNewToken?: boolean
  ) => void;
}

export const AuthContext = createContext<Auth>({
  authenticated: false,
  user: null,
  login: async () => false,
  logout: async () => {},
  silentLogout: () => {},
  hasRole: () => false,
  hasResourceAccess: () => false,
  exceedsLimit: () => false,
  validUntil: () => null,
  getLimit: () => 0,
  updateTokens: (
    access_token: string,
    refresh_token: string,
    updateUserWithNewToken?: boolean
  ) => {},
});

const AuthProvider: FC<Props> = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const tokenRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getTokens = () => {
    const accessToken = localStorage.getItem('access_token') || null;
    const refreshToken = localStorage.getItem('refresh_token') || null;
    return {
      accessToken,
      refreshToken,
    };
  };

  const updateTokens = async (
    access_token: string,
    refresh_token: string,
    updateUserWithNewToken: boolean = false
  ) => {
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);

    if (updateUserWithNewToken) {
      await updateUser(access_token);
    }
  };

  const hasRole = (realmRole: string) => {
    const { accessToken } = getTokens();

    if (accessToken) {
      const decodedToken = jwtDecode<IToken>(accessToken);
      if (!decodedToken.realm_access) return false;
      return decodedToken.realm_access.roles.includes(realmRole);
    }

    return false;
  };

  const hasResourceAccess = (type: 'realm-management' | 'account', roleName: string) => {
    const { accessToken } = getTokens();

    if (accessToken) {
      const decodedToken = jwtDecode<IToken>(accessToken);
      console.log(decodedToken.resource_access);

      if (!decodedToken.resource_access || !decodedToken.resource_access[type]) return false;
      console.log(decodedToken.resource_access[type].roles, roleName);
      return decodedToken.resource_access[type].roles.includes(roleName);
    }

    return false;
  };

  const validUntil = () => {
    const { accessToken } = getTokens();

    if (accessToken) {
      const decodedToken = jwtDecode<IToken>(accessToken);
      if (typeof decodedToken.validuntil === 'undefined') {
        return null;
      }
      const validuntil = decodedToken.validuntil;
      if (validuntil) {
        return new Date(validuntil);
      }
    }

    return null;
  };

  const exceedsLimit = (limitName: string, testValue: number) => {
    const { accessToken } = getTokens();

    if (accessToken) {
      const decodedToken = jwtDecode<IToken>(accessToken);
      if (typeof decodedToken[limitName] === 'undefined') {
        return false;
      }
      const limit = decodedToken[limitName];
      return testValue > limit;
    }

    return false;
  };

  const getLimit = (limitName: string, defaultValue: number) => {
    const { accessToken } = getTokens();

    if (accessToken) {
      const decodedToken = jwtDecode<IToken>(accessToken);
      if (typeof decodedToken[limitName] === 'undefined') {
        return defaultValue;
      }
      return decodedToken[limitName];
    }

    return 0;
  };

  const performAuthCleanup = () => {
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setAuthenticated(false);
    setUser(null);
  };

  const login = async (
    username: string,
    password: string,
    loading: Dispatch<SetStateAction<boolean>>
  ) => {
    const response = await AuthService.login(username, password, loading);

    if (typeof response === 'boolean') return response;

    const { access_token, refresh_token } = response;

    if (access_token && refresh_token) {
      updateTokens(access_token, refresh_token);
      const decodedToken = jwtDecode<IToken>(access_token);
      if (!decodedToken.exp) return false;
      const expiresIn = (decodedToken.exp - new Date().getTime() / 1000) * 1000 - 10000;
      tokenRefreshTimerRef.current = setTimeout(() => refresh(refresh_token), expiresIn);
      await updateUser(access_token);
      setAuthenticated(true);
    }

    return true;
  };

  const logout = async () => {
    const { accessToken, refreshToken } = getTokens();

    if (accessToken && refreshToken) {
      await AuthService.logout(accessToken, refreshToken);
      performAuthCleanup();
      window.location.replace(window.location.origin);
    }
  };

  const silentLogout = () => {
    const { accessToken, refreshToken } = getTokens();

    if (accessToken && refreshToken) {
      performAuthCleanup();
      window.location.replace(window.location.origin);
    }
  };

  const updateUser = async (access_token: string) => {
    const data = await AuthService.loadUserProfile(access_token);
    if (!data) return;
    setUser(data);
  };

  const refresh = useCallback(async (refreshToken: string, signal?: AbortSignal) => {
    const tokens = getTokens();
    const response = await AuthService.refreshToken(tokens.refreshToken ?? refreshToken, signal);

    if (!response) {
      performAuthCleanup();
      return;
    }

    const { access_token, refresh_token } = response;

    if (access_token && refresh_token) {
      updateTokens(access_token, refresh_token);
      const decodedToken = jwtDecode<IToken>(access_token);
      if (!decodedToken.exp) return;
      const expiresIn = (decodedToken.exp - new Date().getTime() / 1000) * 1000 - 10000;
      tokenRefreshTimerRef.current = setTimeout(() => refresh(refresh_token), expiresIn);
      await updateUser(access_token);
      setAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    const { accessToken, refreshToken } = getTokens();
    const controller = new AbortController();

    if (accessToken && refreshToken) {
      (async () => await refresh(refreshToken, controller.signal))();
    }

    return () => {
      controller.abort();
    };
  }, [refresh]);

  useEffect(() => {
    const forceCleanup = (event: StorageEvent) => {
      if (
        (event.key === 'access_token' || event.key === 'refresh_token') &&
        event.oldValue &&
        !event.newValue
      ) {
        performAuthCleanup();
        window.location.replace(window.location.origin);
      }
    };

    window.addEventListener('storage', (e) => {
      forceCleanup(e);
    });

    return () => {
      window.removeEventListener('storage', (e) => {
        forceCleanup(e);
      });
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        authenticated,
        user,
        login,
        logout,
        silentLogout,
        hasRole,
        exceedsLimit,
        validUntil,
        getLimit,
        updateTokens,
        hasResourceAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
